/**
 * Inline AI-evidence highlighting for a rendered job document — shared.
 *
 * Faithful extraction of rm-portal's highlight logic from
 * `useClassificationAiAssist.js`, with the Vue/state coupling removed: this module
 * only ever takes a DOM container plus a list of evidence sentences, so both
 * portals highlight identically. No Vue import, no refs.
 *
 * Why it's this involved: the AI returns sentences as prose, but the document is
 * arbitrary author-authored HTML. A sentence routinely spans several text nodes
 * (`<strong>`, links, list items), and the two strings differ in ways that break a
 * naive `indexOf`:
 *   · smart quotes, en/em dashes, non-breaking and exotic spaces, bullets
 *   · zero-width characters
 *   · collapsed whitespace runs
 *   · trailing punctuation the AI adds that the DOM doesn't have (`.` vs `;`)
 *
 * So matching runs against a NORMALISED projection of the document text that keeps
 * an index map back to the original, and marks are then applied by splitting the
 * live text nodes the match actually covers.
 */

/* Character classes that need folding before matching. Written as \u escapes: most
   of these are invisible or visually identical to their ASCII counterparts, so a
   literal would be unreadable and trivially corrupted in transit. */
const ZERO_WIDTH = /[​-‍­⁠﻿]/;
const WHITESPACE = /[  -   　]/;
const SMART_SINGLE = /[‘’‚‹›]/;
const SMART_DOUBLE = /[“”„«»]/;
const DASHES = /[–—―‒]/;
const BULLETS = /[•‣◦⁃∙]/;
const ELLIPSIS = '…';

export const HIGHLIGHT_CLASS = 'ai-highlight';
const TOOLTIP_CLASS = 'ai-highlight-tooltip';

/**
 * Normalise a string for matching while keeping a map back to the original.
 * @returns {{ normalized: string, toOriginal: number[] }} where `toOriginal[i]` is
 *          the original index that produced normalised char `i`.
 */
export function buildNormalizedMap(str) {
  const out = [];
  const toOriginal = [];

  for (let i = 0; i < str.length; i++) {
    const ch = str[i];

    // Zero-width characters contribute nothing.
    if (ZERO_WIDTH.test(ch)) continue;

    // An ellipsis character stands in for three dots.
    if (ch === ELLIPSIS) {
      out.push('.', '.', '.');
      toOriginal.push(i, i, i);
      continue;
    }

    let mapped = ch;
    if (WHITESPACE.test(ch)) mapped = ' ';
    else if (SMART_SINGLE.test(ch)) mapped = "'";
    else if (SMART_DOUBLE.test(ch)) mapped = '"';
    else if (DASHES.test(ch)) mapped = '-';
    else if (BULLETS.test(ch)) mapped = '-';

    // Collapse runs of whitespace to a single space.
    if (mapped === ' ' && out.length > 0 && out[out.length - 1] === ' ') continue;

    out.push(mapped);
    toOriginal.push(i);
  }

  return { normalized: out.join(''), toOriginal };
}

/** Normalise a needle; no position map needed. */
export function normalizeForMatch(str) {
  return buildNormalizedMap(str).normalized;
}

/** Every text node under `container`, in document order. */
function collectTextNodes(container) {
  const nodes = [];
  const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  while (walker.nextNode()) nodes.push(walker.currentNode);
  return nodes;
}

/** Offset-indexed view of the container's text nodes. */
function segmentsOf(nodes) {
  const segments = [];
  let offset = 0;
  for (const node of nodes) {
    segments.push({ node, start: offset, length: node.nodeValue.length });
    offset += node.nodeValue.length;
  }
  return segments;
}

/** Unwrap every existing highlight, restoring the original text nodes. */
export function removeHighlights(container) {
  if (!container) return;
  container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}`).forEach((mark) => {
    const parent = mark.parentNode;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
}

/** The tooltip body for one evidence item: sub-factor, confidence, justification. */
function tooltipFor(item) {
  if (!item || typeof item !== 'object') return '';
  const parts = [];
  if (item.sub_factor) parts.push(item.sub_factor);
  if (item.confidence != null) parts.push(`Confidence: ${Math.round(item.confidence * 100)}%`);
  if (item.justification) parts.push(item.justification);
  return parts.join('\n');
}

/* One tooltip element at a time, appended to body so it escapes the panel's
   overflow. Module-scoped because only one can be visible. */
let activeTooltipEl = null;

function hideTooltip() {
  if (activeTooltipEl) {
    activeTooltipEl.remove();
    activeTooltipEl = null;
  }
}

function showTooltip(e) {
  hideTooltip();
  const mark = e.currentTarget;
  const text = mark.getAttribute('data-tooltip');
  if (!text) return;

  const doc = mark.ownerDocument;
  const tip = doc.createElement('div');
  tip.className = TOOLTIP_CLASS;
  tip.textContent = text;
  doc.body.appendChild(tip);
  activeTooltipEl = tip;

  // Clamp into the viewport, flipping below the mark when there's no room above.
  const rect = mark.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const win = doc.defaultView;
  let left = rect.left + rect.width / 2 - tipRect.width / 2;
  left = Math.max(8, Math.min(left, win.innerWidth - tipRect.width - 8));
  let top = rect.top - tipRect.height - 6;
  if (top < 8) top = rect.bottom + 6;

  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

function attachTooltips(container) {
  container.querySelectorAll(`mark.${HIGHLIGHT_CLASS}[data-tooltip]`).forEach((mark) => {
    mark.addEventListener('mouseenter', showTooltip);
    mark.addEventListener('mouseleave', hideTooltip);
  });
}

/**
 * Wrap every matched evidence sentence in `container` with
 * `<mark class="ai-highlight">`, replacing any previous highlighting.
 *
 * @param {HTMLElement} container the rendered document
 * @param {Array} sentences evidence items — objects with `.sentence` (plus optional
 *        `sub_factor` / `confidence` / `justification` for the tooltip), or strings
 * @returns {number} how many sentences were actually located
 */
export function applyHighlights(container, sentences) {
  if (!container) return 0;
  removeHighlights(container);
  hideTooltip();
  const items = sentences || [];
  if (!items.length) return 0;

  // Phase 1 — resolve every match against the CLEAN document text, before any
  // mutation, so offsets are all in the same coordinate space.
  const textNodes = collectTextNodes(container);
  const fullText = textNodes.map((n) => n.nodeValue).join('');
  const { normalized, toOriginal } = buildNormalizedMap(fullText);
  const haystack = normalized.toLowerCase();

  const matches = [];
  for (const item of items) {
    const raw = (typeof item === 'string' ? item : item?.sentence || '').trim();
    if (!raw) continue;

    let needle = normalizeForMatch(raw).toLowerCase();
    let idx = haystack.indexOf(needle);

    // The AI commonly appends punctuation the document doesn't carry (a full stop
    // where a list item ends in a semicolon). Retry once without it.
    if (idx === -1 && /[.;:,!?]$/.test(needle)) {
      needle = needle.slice(0, -1);
      idx = haystack.indexOf(needle);
    }
    if (idx === -1) continue;

    const matchStart = toOriginal[idx];
    const matchEnd =
      idx + needle.length < toOriginal.length ? toOriginal[idx + needle.length] : fullText.length;

    matches.push({ matchStart, matchEnd, tooltipText: tooltipFor(item) });
  }

  if (!matches.length) return 0;

  // Phase 2 — apply descending, so a mutation never shifts an offset we still need.
  // Text nodes are re-walked per match because splitText invalidates references;
  // wrapping in <mark> doesn't change the concatenated text, so offsets stay valid.
  matches.sort((a, b) => b.matchStart - a.matchStart);

  for (const { matchStart, matchEnd, tooltipText } of matches) {
    const liveSegments = segmentsOf(collectTextNodes(container));

    const affected = [];
    for (const seg of liveSegments) {
      const segEnd = seg.start + seg.length;
      if (seg.start < matchEnd && segEnd > matchStart) {
        affected.push({
          node: seg.node,
          localStart: Math.max(0, matchStart - seg.start),
          localEnd: Math.min(seg.length, matchEnd - seg.start),
        });
      }
    }

    // Rightmost node first, for the same offset-stability reason.
    for (let i = affected.length - 1; i >= 0; i--) {
      const { node, localStart, localEnd } = affected[i];
      const mark = container.ownerDocument.createElement('mark');
      mark.className = HIGHLIGHT_CLASS;
      if (tooltipText) {
        mark.setAttribute('data-tooltip', tooltipText);
        mark.style.cursor = 'pointer';
      }
      const matchNode = node.splitText(localStart);
      matchNode.splitText(localEnd - localStart);
      matchNode.parentNode.replaceChild(mark, matchNode);
      mark.appendChild(matchNode);
    }
  }

  attachTooltips(container);
  return matches.length;
}

/**
 * Apply highlights once the container actually has text. The document renders
 * asynchronously, so a straight call can land before there is anything to match.
 * Watches for content, then applies; gives up after `timeoutMs`.
 *
 * @returns {Function} a cancel function
 */
export function applyHighlightsWhenReady(container, sentences, timeoutMs = 5000) {
  if (!container) return () => {};
  if (container.textContent.trim().length > 0) {
    applyHighlights(container, sentences);
    return () => {};
  }
  const observer = new MutationObserver(() => {
    if (container.textContent.trim().length > 0) {
      observer.disconnect();
      applyHighlights(container, sentences);
    }
  });
  observer.observe(container, { childList: true, subtree: true });
  const timer = setTimeout(() => observer.disconnect(), timeoutMs);
  return () => {
    observer.disconnect();
    clearTimeout(timer);
  };
}

/** Tear down any visible tooltip (call on unmount). */
export function resetHighlightTooltips() {
  hideTooltip();
}
